"use client";

import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import {
  HELP_FAB_SIZE_PX,
  clampHelpFabPosition,
  getHelpFabDefaultPosition,
  type HelpFabPoint,
} from "@mcblessing1/in-app-feedback";

const DRAG_THRESHOLD_PX = 8;
/** Swallow the compatibility `click` that follows touch `pointerup` (mobile ghost click). */
const GHOST_CLICK_GUARD_MS = 500;

type ProductHelpFabProps = {
  onOpen: () => void;
  hidden?: boolean;
};

/**
 * After opening on pointerup, the FAB often unmounts before the browser's
 * synthetic click. That click would hit whatever is now under the finger
 * (Help menu rows). Arm a one-shot capture listener to drop that click.
 */
function armGhostClickGuard(timeoutMs = GHOST_CLICK_GUARD_MS): () => void {
  let cleared = false;
  const clear = () => {
    if (cleared) return;
    cleared = true;
    document.removeEventListener("click", suppress, true);
    window.clearTimeout(timer);
  };
  const suppress = (ev: MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    clear();
  };
  document.addEventListener("click", suppress, true);
  const timer = window.setTimeout(clear, timeoutMs);
  return clear;
}

export default function ProductHelpFab({
  onOpen,
  hidden,
}: ProductHelpFabProps) {
  const [pos, setPos] = useState<HelpFabPoint | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);
  const disarmGhostClickRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const placeDefault = () => {
      setPos((prev) => {
        if (prev) {
          return clampHelpFabPosition(
            prev.left,
            prev.top,
            window.innerWidth,
            window.innerHeight,
          );
        }
        return getHelpFabDefaultPosition(window.innerWidth, window.innerHeight);
      });
    };
    placeDefault();
    window.addEventListener("resize", placeDefault);
    return () => window.removeEventListener("resize", placeDefault);
  }, []);

  // Do not clear the ghost-click guard on FAB unmount — open hides the FAB
  // before the synthetic click arrives; the guard self-clears on click/timeout.

  if (hidden || !pos) return null;

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originLeft: pos.left,
      originTop: pos.top,
      moved: false,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    drag.moved = true;
    if (!dragging) setDragging(true);

    setPos(
      clampHelpFabPosition(
        drag.originLeft + dx,
        drag.originTop + dy,
        window.innerWidth,
        window.innerHeight,
      ),
    );
  };

  const endPointer = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const wasDrag = drag.moved;
    dragRef.current = null;
    setDragging(false);

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }

    if (wasDrag) return;

    // Open on pointerup for drag/tap split; guard the follow-up click.
    disarmGhostClickRef.current?.();
    disarmGhostClickRef.current = armGhostClickGuard();
    onOpen();
  };

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      aria-label="Open help"
      title="Help"
      data-dragging={dragging ? "true" : undefined}
      style={{
        left: pos.left,
        top: pos.top,
        width: HELP_FAB_SIZE_PX,
        height: HELP_FAB_SIZE_PX,
        touchAction: "none",
      }}
      className="help-fab-beam fixed z-[10020] inline-flex cursor-grab items-center justify-center rounded-full border border-border bg-surface/95 text-foreground shadow-lg backdrop-blur hover:bg-surface hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:cursor-grabbing"
    >
      <QuestionMarkCircleIcon className="h-7 w-7" aria-hidden />
    </button>
  );
}
